import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { useSubmitFeatureRequest } from "@/hooks/useAdmin";
import { getApiErrorMessage } from "@/lib/api-error";

/**
 * Task #138 — let teachers tell us what they want next without leaving
 * the app. Captured to `feature_requests` and surfaced to the operator
 * via the admin "Features" tab. Free-text only, hard-capped to 2000
 * chars (mirrors the server-side Zod limit in routes/feature-requests.ts).
 */
export function FeatureRequestDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [body, setBody] = useState("");
  const { toast } = useToast();
  const submit = useSubmitFeatureRequest();

  const remaining = 2000 - body.length;
  const trimmed = body.trim();
  const canSubmit = trimmed.length >= 5 && trimmed.length <= 2000 && !submit.isPending;

  async function handleSubmit() {
    if (!canSubmit) return;
    try {
      await submit.mutateAsync(trimmed);
      toast({
        title: "Thanks!",
        description: "We read every suggestion. We won't reply individually, but it goes straight to the team.",
      });
      setBody("");
      onOpenChange(false);
    } catch (err) {
      toast({
        title: "Couldn't send",
        description: getApiErrorMessage(err, "Please try again later."),
        variant: "destructive",
      });
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Suggest a feature</DialogTitle>
          <DialogDescription>
            What would make ClassTrack more useful for your day-to-day teaching?
            Bug reports welcome too.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Textarea
            value={body}
            onChange={(e) => setBody(e.target.value.slice(0, 2000))}
            placeholder="e.g. it would be great if I could…"
            rows={6}
            maxLength={2000}
          />
          <div className="text-xs text-gray-500 text-right">
            {remaining} characters remaining
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit}>
            {submit.isPending ? "Sending…" : "Send suggestion"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
